import { createTheme } from '@mui/material/styles';

export const themeOptions = {
  palette: {
    mode: 'light',
    primary: {
      main: '#61001f',
      dark: '#400012',
      light: '#ffd9dd',
      container: '#8a0030',
      fixed: '#ffd9dd',
    },
    secondary: {
      main: '#255dad',
      dark: '#003c7e',
      light: '#d7e2ff',
      container: '#79a9fd',
    },
    tertiary: {
      main: '#5c0e02',
      container: '#7b2515',
    },
    grey: {
      main: '#a8a8a8',
    },
    danger: {
      main: '#ba1a1a',
    },
    white: {
      main: '#fffaf5',
    },
    background: {
      default: '#fcf9f5',
      paper: '#ffffff',
      surface: '#fcf9f5',
      surfaceLow: '#f6f3ef',
      surfaceContainer: '#f0edea',
      surfaceHigh: '#ebe8e4',
      surfaceHighest: '#e5e2de',
    },
    text: {
      primary: '#1c1c1a',
      secondary: '#655b5b',
    },
    outline: {
      main: '#938585',
      variant: '#e3dcd8',
    },
  },
  typography: {
    fontFamily: '"Plus Jakarta Sans", Roboto, sans-serif',
    h1: {
      fontFamily: '"Plus Jakarta Sans", sans-serif',
      fontSize: '32px',
      lineHeight: '40px',
      fontWeight: 700,
      letterSpacing: '-0.02em',
    },
    h2: {
      fontFamily: '"Plus Jakarta Sans", sans-serif',
      fontSize: '28px',
      lineHeight: '36px',
      fontWeight: 600,
      letterSpacing: '-0.01em',
    },
    h3: {
      fontFamily: '"Plus Jakarta Sans", sans-serif',
      fontSize: '22px',
      lineHeight: '30px',
      fontWeight: 600,
    },
    h4: {
      fontFamily: '"Plus Jakarta Sans", sans-serif',
      fontSize: '18px',
      lineHeight: '26px',
      fontWeight: 600,
    },
    body1: {
      fontFamily: '"Plus Jakarta Sans", sans-serif',
      fontSize: '16px',
      lineHeight: '26px',
    },
    body2: {
      fontFamily: '"Plus Jakarta Sans", sans-serif',
      fontSize: '14px',
      lineHeight: '22px',
    },
    button: {
      fontFamily: '"Plus Jakarta Sans", Roboto, sans-serif',
      fontSize: '14px',
      lineHeight: '20px',
      fontWeight: 600,
      textTransform: 'none',
    },
    caption: {
      fontFamily: '"Plus Jakarta Sans", Roboto, sans-serif',
      fontSize: '12px',
      lineHeight: '16px',
      fontWeight: 600,
    },
  },
  shape: {
    borderRadius: 12,
  },
  shadows: [
    'none',
    '0px 4px 20px rgba(29, 29, 27, 0.05)',
    '0px 10px 30px rgba(29, 29, 27, 0.10)',
    '0px 16px 40px rgba(29, 29, 27, 0.12)',
    '0px 18px 48px rgba(29, 29, 27, 0.14)',
    '0px 20px 56px rgba(29, 29, 27, 0.16)',
    '0px 22px 64px rgba(29, 29, 27, 0.18)',
    '0px 24px 72px rgba(29, 29, 27, 0.20)',
    '0px 26px 80px rgba(29, 29, 27, 0.22)',
    '0px 28px 88px rgba(29, 29, 27, 0.24)',
    '0px 30px 96px rgba(29, 29, 27, 0.26)',
    '0px 32px 104px rgba(29, 29, 27, 0.28)',
    '0px 34px 112px rgba(29, 29, 27, 0.30)',
    '0px 36px 120px rgba(29, 29, 27, 0.32)',
    '0px 38px 128px rgba(29, 29, 27, 0.34)',
    '0px 40px 136px rgba(29, 29, 27, 0.36)',
    '0px 42px 144px rgba(29, 29, 27, 0.38)',
    '0px 44px 152px rgba(29, 29, 27, 0.40)',
    '0px 46px 160px rgba(29, 29, 27, 0.42)',
    '0px 48px 168px rgba(29, 29, 27, 0.44)',
    '0px 50px 176px rgba(29, 29, 27, 0.46)',
    '0px 52px 184px rgba(29, 29, 27, 0.48)',
    '0px 54px 192px rgba(29, 29, 27, 0.50)',
    '0px 56px 200px rgba(29, 29, 27, 0.52)',
    '0px 58px 208px rgba(29, 29, 27, 0.54)',
  ],
  dimensions: {
    tiny: '15px',
    small: '30px',
    medium: '45px',
    large: '60px',
    huge: '90px',
  },
};

const theme = createTheme({
  ...themeOptions,
  components: {
    MuiCssBaseline: {
      styleOverrides: {
        body: {
          backgroundColor: '#fcf9f5',
          color: '#1c1c1a',
        },
      },
    },
    MuiButton: {
      defaultProps: { disableElevation: true },
      styleOverrides: {
        root: {
          borderRadius: 10,
          minHeight: 44,
          padding: '8px 16px',
          fontWeight: 600,
          lineHeight: 1.4,
          boxShadow: 'none',
          '&.Mui-focusVisible': { outline: '2px solid #8a0030', outlineOffset: 3 },
        },
      },
    },
    MuiTextField: {
      defaultProps: {
        variant: 'outlined',
        size: 'small',
      },
    },
    MuiOutlinedInput: {
      styleOverrides: {
        root: {
          borderRadius: 10,
          backgroundColor: '#ffffff',
          '& fieldset': {
            borderColor: '#e3dcd8',
          },
          '&:hover fieldset': {
            borderColor: '#938585',
          },
          '&.Mui-focused fieldset': {
            borderColor: '#61001f',
            borderWidth: 2,
          },
        },
      },
    },
    MuiInputLabel: {
      styleOverrides: { root: { color: '#655b5b' } },
    },
    MuiDialog: {
      styleOverrides: { paper: { borderRadius: 18, boxShadow: '0 12px 40px rgba(29,29,27,0.12)' } },
    },
    MuiDialogTitle: {
      styleOverrides: { root: { fontSize: 20, fontWeight: 600, lineHeight: 1.4 } },
    },
    MuiDialogActions: {
      styleOverrides: { root: { padding: '12px 24px 20px', gap: 8 } },
    },
    MuiChip: {
      styleOverrides: { root: { fontWeight: 500, borderRadius: 8 } },
    },
    MuiPaper: {
      styleOverrides: {
        root: {
          backgroundImage: 'none',
        },
      },
    },
  },
});

export default theme;
